import React, { useMemo } from 'react';

import { IconButton } from '@picsio/ui';
import {
  ArrowBottomSmall,
  ArrowTopSmall,
  Close,
  AddMoreFilters,
  SearchPlusIcon,
} from '@picsio/icons';
import cn from 'classnames';

import ToolbarSort from '../../Sort';
import CatalogViewMode from '../../CatalogViewMode';
import SearchFilterItem from '../SearchFilterItem';
import picsioConfig from '../../../../../../config';
import { isRouteProducts } from '../../../helpers/history';
import Tooltip from '../../Tooltip';
import AddFiltersBtn from '../AddFiltersBtn';
import l10n from '../../../shared/strings';
import { getMixedFilterItemRef } from './helpers/getMixedFilterItemRef';
import './styles.scss';

const SearchFiltersToriut = ({
  onlyTextSearch,
  customizingMode,
  containerRef,
  handleMouseEnter,
  handleMouseLeave,
  isHovering,
  hiddenItemsCount,
  hiddenMediaCount = 0,
  hiddenProductCount = 0,
  isCollapsed,
  expand,
  collapse,
  controlsRef,
  resetIsHidden,
  hiddenItemsHaveActiveFilter,
  items,
  renderItems,
  handleResetAllFilters,
  handleCustomizingMode,
  handleSavedSearchSave,
  handleSelectFilter,
  handleConfirmFilter,
  handleResetFilter,
  handleOpen,
  handleClose,
  handleRemoveFilter,
  dragstart,
  dragenter,
  dragend,
  FILTERS,
  rootCollection,
  itemsMediaRef,
  itemsProductsRef,
  hiddenItemsCounterRef,
  sortType,
  handleChangeSort,
  handleShowAddFiltersDialog,
  isRouteSearch,
  location,
  selectedFilters,
  confirmedFilters,
  checkActive,
  checkOpen,
  renderItemsProducts,
  pinnedFilterIds = [],
  isMediaLibraryModal = false,
}) => {
  // Helper function to get margin class based on state
  const getMarginClass = (isCustomizing, renderItemsList) => {
    if (isCustomizing && renderItemsList.length > 0) return 'toriut-search-filters__product-container--margin-small';
    if (!isCustomizing) return 'toriut-search-filters__product-container--margin-none';
    return 'toriut-search-filters__product-container--margin-large';
  };

  const mixCollapsedRows = isCollapsed && !customizingMode && !isMediaLibraryModal;
  const sectionedCollapsed = isCollapsed && !customizingMode && isMediaLibraryModal;
  const collapsedRowItems = useMemo(() => (
    items.length ? items : [...renderItems, ...renderItemsProducts]
  ), [items, renderItems, renderItemsProducts]);
  let mediaRowItems = renderItems;
  let productRowItems = renderItemsProducts;
  if (mixCollapsedRows) {
    mediaRowItems = collapsedRowItems;
    productRowItems = collapsedRowItems;
  } else if (sectionedCollapsed) {
    mediaRowItems = renderItems.slice(
      0,
      Math.max(0, renderItems.length - hiddenMediaCount),
    );
    productRowItems = renderItemsProducts.slice(
      0,
      Math.max(0, renderItemsProducts.length - hiddenProductCount),
    );
  }

  const showProductFilters = useMemo(() => {
    if (mixCollapsedRows && renderItems.length > 0) return false;
    if (sectionedCollapsed && !renderItemsProducts.length) return false;
    return true;
  }, [mixCollapsedRows, renderItems, renderItemsProducts.length, sectionedCollapsed]);

  const showExpandControl = isHovering
    && !customizingMode
    && (renderItems.length > 0 || renderItemsProducts.length > 0)
    && (hiddenItemsCount > 0 || isMediaLibraryModal);

  return (
    <div
      ref={containerRef}
      className={cn('toriut-search-filters', {
        'toriut-search-filters--modal-collapsed-columns': sectionedCollapsed,
        'toriut-search-filters--modal-expanded': (
          isMediaLibraryModal && !customizingMode && !isCollapsed
        ),
      })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <If condition={!onlyTextSearch}>
        <div className="toriut-search-filters__media-section">
          <If condition={picsioConfig.isPim && renderItems.length > 0}>
            <If condition={customizingMode}>
              <div className="toriut-search-filters__media-divider toriut-search-filters__media-divider--customizing">
                {l10n.SEARCH.mediaFilters}
              </div>
            </If>
            <div
              className={cn('toriut-search-filters__media-container', {
                'toriut-search-filters__media-container--flex': customizingMode,
                'toriut-search-filters__media-container--inline': !customizingMode,
                'toriut-search-filters__media-container--has-products': renderItems.length > 0 && renderItemsProducts.length > 0,
                'toriut-search-filters__media-container--customizing': customizingMode,
                'toriut-search-filters__media-container--collapsed-single-line': sectionedCollapsed,
              })}
            >
              <If condition={!customizingMode && !sectionedCollapsed && !isMediaLibraryModal}>
                <div ref={controlsRef} className="toriut-search-filters__controls">
                  <If condition={picsioConfig.isMainApp}>
                    <Choose>
                      <When condition={isMediaLibraryModal && isCollapsed && !resetIsHidden}>
                        <div
                          data-id="resetFilters"
                          className="toriut-search-filters__reset-link"
                        >
                          <span
                            className="picsioLink"
                            onClick={(e) => handleResetAllFilters(e, 'minimizedView')}
                          >
                            {l10n.SEARCH.resetFiltersBtn}
                          </span>
                        </div>
                      </When>
                      <When condition={isCollapsed && !resetIsHidden}>
                        <Tooltip
                          content={l10n.SEARCH.resetFiltersBtn}
                          placement="top"
                        >
                          <IconButton
                            size="md"
                            className="toriut-search-filters__control-button toriut-search-filters__control-button--add-more"
                            color="primary"
                            onClick={(e) => handleResetAllFilters(e, 'minimizedView')}
                          >
                            <Close />
                          </IconButton>
                        </Tooltip>
                      </When>
                      <Otherwise>
                        <Tooltip
                          content={l10n.SEARCH.addMoreFilters}
                          placement="top"
                        >
                          <IconButton
                            size="md"
                            className="toriut-search-filters__control-button toriut-search-filters__control-button--add-more"
                            onClick={handleCustomizingMode}
                          >
                            <AddMoreFilters />
                          </IconButton>
                        </Tooltip>
                      </Otherwise>
                    </Choose>
                    <If condition={!isMediaLibraryModal}>
                      <Tooltip
                        content={l10n.SEARCH.saveSearch}
                        placement="top"
                      >
                        <IconButton
                          size="md"
                          className="toriut-search-filters__control-button"
                          disabled={!Object.keys(selectedFilters).length}
                          onClick={handleSavedSearchSave}
                        >
                          <SearchPlusIcon />
                        </IconButton>
                      </Tooltip>
                    </If>
                  </If>
                  <If condition={isRouteSearch(location.path)}>
                    <Tooltip
                      content={l10n.SEARCH.sort}
                      placement="top"
                    >
                      <div>
                        <ToolbarSort
                          sortType={sortType}
                          changeSort={handleChangeSort}
                          openOnClick
                        />
                      </div>
                    </Tooltip>
                  </If>
                  <If condition={!isMediaLibraryModal && !isRouteProducts(location.pathname)}>
                    <Tooltip
                      content={l10n.SEARCH.view}
                      placement="top"
                    >
                      <div>
                        <CatalogViewMode openOnClick leftFilter />
                      </div>
                    </Tooltip>
                  </If>
                </div>
              </If>
              <div
                className="toriut-search-filters__drop-zone"
                data-id="first"
                onDragEnter={dragenter}
              />
              <If condition={!customizingMode && picsioConfig.isPim && (!isCollapsed || isMediaLibraryModal)}>
                <div className="toriut-search-filters__media-divider">
                  {l10n.SEARCH.mediaFilters}
                </div>
              </If>
              <span />
              <Choose>
                <When condition={sectionedCollapsed}>
                  <div className="toriut-search-filters__chip-row">
                    {
                      mediaRowItems.map((item, i) => (
                        <SearchFilterItem
                          key={item.id}
                          ref={getMixedFilterItemRef({
                            isCollapsedRow: mixCollapsedRows,
                            index: i,
                            mediaCount: renderItems.length,
                            itemsMediaRef,
                            itemsProductsRef,
                          })}
                          id={item.id}
                          data={item}
                          filterData={selectedFilters[item.id]}
                          confirmedData={confirmedFilters[item.id]}
                          customizingMode={customizingMode}
                          isSelected={checkActive(item.id)}
                          isOpen={checkOpen(item.id)}
                          removable={!pinnedFilterIds.includes(item.id)}
                          filters={FILTERS}
                          rootCollectionId={rootCollection?._id}
                          stackAboveModals={isMediaLibraryModal}
                          onSelectFilter={handleSelectFilter}
                          onConfirmFilter={handleConfirmFilter}
                          onResetFilter={handleResetFilter}
                          onOpen={handleOpen}
                          onClose={handleClose}
                          onRemove={handleRemoveFilter}
                          onDragStart={dragstart}
                          onDragEnter={dragenter}
                          onDragEnd={dragend}
                        />
                      ))
                    }
                    <If condition={hiddenMediaCount}>
                      <Tooltip content={l10n.RESIZER.expandFilters} placement="top">
                        <div
                          ref={hiddenItemsCounterRef}
                          data-id="hiddenItems"
                          className="toriut-search-filters__hidden-counter"
                          onClick={(e) => expand(e, 'aggregatedBadgeClick')}
                        >
                          <div className={cn('toriut-search-filters__filter-item', {
                            'toriut-search-filters__filter-item--active': !hiddenItemsHaveActiveFilter,
                          })}
                          >
                            <div className="toriut-search-filters__filter-title">{`+${hiddenMediaCount}`}</div>
                          </div>
                        </div>
                      </Tooltip>
                    </If>
                  </div>
                </When>
                <Otherwise>
                  {
                    mediaRowItems.map((item, i) => (
                      <SearchFilterItem
                        key={item.id}
                        ref={getMixedFilterItemRef({
                          isCollapsedRow: mixCollapsedRows,
                          index: i,
                          mediaCount: renderItems.length,
                          itemsMediaRef,
                          itemsProductsRef,
                        })}
                        id={item.id}
                        data={item}
                        filterData={selectedFilters[item.id]}
                        confirmedData={confirmedFilters[item.id]}
                        customizingMode={customizingMode}
                        isSelected={checkActive(item.id)}
                        isOpen={checkOpen(item.id)}
                        removable={!pinnedFilterIds.includes(item.id)}
                        filters={FILTERS}
                        rootCollectionId={rootCollection?._id}
                        stackAboveModals={isMediaLibraryModal}
                        onSelectFilter={handleSelectFilter}
                        onConfirmFilter={handleConfirmFilter}
                        onResetFilter={handleResetFilter}
                        onOpen={handleOpen}
                        onClose={handleClose}
                        onRemove={handleRemoveFilter}
                        onDragStart={dragstart}
                        onDragEnter={dragenter}
                        onDragEnd={dragend}
                      />
                    ))
                  }
                  <If condition={mixCollapsedRows && hiddenItemsCount}>
                    <Tooltip content={l10n.RESIZER.expandFilters} placement="top">
                      <div
                        ref={hiddenItemsCounterRef}
                        data-id="hiddenItems"
                        className="toriut-search-filters__hidden-counter"
                        onClick={(e) => expand(e, 'aggregatedBadgeClick')}
                      >
                        <div className={cn('toriut-search-filters__filter-item', {
                          'toriut-search-filters__filter-item--active': !hiddenItemsHaveActiveFilter,
                        })}
                        >
                          <div className="toriut-search-filters__filter-title">{`+${hiddenItemsCount}`}</div>
                        </div>
                      </div>
                    </Tooltip>
                  </If>
                </Otherwise>
              </Choose>
              <If condition={picsioConfig.isMainApp && renderItemsProducts.length === 0}>
                <If condition={customizingMode}>
                  <AddFiltersBtn onAddFilters={handleShowAddFiltersDialog} />
                </If>
              </If>
              <If condition={!customizingMode && Object.keys(confirmedFilters).length && !resetIsHidden && !isCollapsed && renderItemsProducts.length === 0}>
                <div
                  data-id="resetFilters"
                  className="toriut-search-filters__reset-link"
                >
                  <span
                    className="picsioLink"
                    onClick={(e) => handleResetAllFilters(e, 'expandedView')}
                  >
                    {l10n.SEARCH.resetFiltersBtn}
                  </span>
                </div>
              </If>
            </div>
          </If>
        </div>

        {/* products filters */}
        <If condition={showProductFilters}>
          <div className="toriut-search-filters__product-section">
            <If condition={picsioConfig.isPim}>
              <If condition={customizingMode}>
                <div className={cn('toriut-search-filters__product-divider toriut-search-filters__product-divider--customizing', {
                  'toriut-search-filters__product-divider--customizing--product-only': !renderItems || renderItems.length === 0,
                  'toriut-search-filters__product-divider--customizing--has-media': customizingMode && renderItems.length > 0,
                })}
                >
                  {l10n.SEARCH.productFilters}
                </div>
              </If>
              <div
                // ref={containerRef}
                className={cn('toriut-search-filters__product-container', {
                  'toriut-search-filters__product-container--flex': customizingMode,
                  'toriut-search-filters__product-container--inline': !customizingMode,
                  'toriut-search-filters__product-container--has-media': renderItemsProducts.length > 0 && renderItems.length > 0,
                  'toriut-search-filters__product-container--customizing': customizingMode,
                  'toriut-search-filters__product-container--collapsed-single-line': sectionedCollapsed,
                  [getMarginClass(customizingMode, renderItems)]: true,
                })}
              >
                <If condition={!customizingMode && renderItems.length === 0 && !sectionedCollapsed && !isMediaLibraryModal}>
                  <div ref={controlsRef} className="toriut-search-filters__controls">
                    <If condition={picsioConfig.isMainApp}>
                      <Choose>
                        <When condition={isMediaLibraryModal && isCollapsed && !resetIsHidden}>
                          <div
                            data-id="resetFilters"
                            className="toriut-search-filters__reset-link"
                          >
                            <span
                              className="picsioLink"
                              onClick={(e) => handleResetAllFilters(e, 'minimizedView')}
                            >
                              {l10n.SEARCH.resetFiltersBtn}
                            </span>
                          </div>
                        </When>
                        <When condition={isCollapsed && !resetIsHidden}>
                          <Tooltip
                            content={l10n.SEARCH.resetFiltersBtn}
                            placement="top"
                          >
                            <IconButton
                              size="md"
                              className="toriut-search-filters__control-button toriut-search-filters__control-button--add-more"
                              color="primary"
                              onClick={(e) => handleResetAllFilters(e, 'minimizedView')}
                            >
                              <Close />
                            </IconButton>
                          </Tooltip>
                        </When>
                        <Otherwise>
                          <Tooltip
                            content={l10n.SEARCH.addMoreFilters}
                            placement="top"
                          >
                            <IconButton
                              size="md"
                              className="toriut-search-filters__control-button toriut-search-filters__control-button--add-more"
                              onClick={handleCustomizingMode}
                            >
                              <AddMoreFilters />
                            </IconButton>
                          </Tooltip>
                        </Otherwise>
                      </Choose>
                      <If condition={!isMediaLibraryModal}>
                        <Tooltip
                          content={l10n.SEARCH.saveSearch}
                          placement="top"
                        >
                          <IconButton
                            size="md"
                            className="toriut-search-filters__control-button"
                            disabled={!Object.keys(selectedFilters).length}
                            onClick={handleSavedSearchSave}
                          >
                            <SearchPlusIcon />
                          </IconButton>
                        </Tooltip>
                      </If>
                    </If>
                    <If condition={isRouteSearch(location.path)}>
                      <Tooltip
                        content={l10n.SEARCH.sort}
                        placement="top"
                      >
                        <div>
                          <ToolbarSort
                            sortType={sortType}
                            changeSort={handleChangeSort}
                            openOnClick
                          />
                        </div>
                      </Tooltip>
                    </If>
                    <If condition={!isMediaLibraryModal && !isRouteProducts(location.pathname)}>
                      <Tooltip
                        content={l10n.SEARCH.view}
                        placement="top"
                      >
                        <div>
                          <CatalogViewMode openOnClick leftFilter />
                        </div>
                      </Tooltip>
                    </If>
                  </div>
                </If>
                <div
                  className={cn('toriut-search-filters__drop-zone', {
                    isAbsolute: renderItemsProducts.length > 0 && !customizingMode && renderItems.length > 0,
                  })}
                  data-id="first"
                  onDragEnter={dragenter}
                />
                <If condition={!customizingMode && (!isCollapsed || isMediaLibraryModal)}>
                  <div className={cn('toriut-search-filters__product-divider', {
                    'toriut-search-filters__product-divider--standalone': !customizingMode && (!renderItems || renderItems.length === 0),
                  })}
                  >
                    {l10n.SEARCH.productFilters}
                  </div>
                </If>
                <Choose>
                  <When condition={sectionedCollapsed}>
                    <div className="toriut-search-filters__chip-row">
                      {
                        productRowItems.map((item, i) => (
                          <SearchFilterItem
                            key={item.id}
                            ref={itemsProductsRef.current[i]}
                            id={item.id}
                            data={item}
                            filterData={selectedFilters[item.id]}
                            confirmedData={confirmedFilters[item.id]}
                            customizingMode={customizingMode}
                            isSelected={checkActive(item.id)}
                            isOpen={checkOpen(item.id)}
                            removable={!pinnedFilterIds.includes(item.id)}
                            filters={FILTERS}
                            rootCollectionId={rootCollection?._id}
                            stackAboveModals={isMediaLibraryModal}
                            onSelectFilter={handleSelectFilter}
                            onConfirmFilter={handleConfirmFilter}
                            onResetFilter={handleResetFilter}
                            onOpen={handleOpen}
                            onClose={handleClose}
                            onRemove={handleRemoveFilter}
                            onDragStart={dragstart}
                            onDragEnter={dragenter}
                            onDragEnd={dragend}
                          />
                        ))
                      }
                      <If condition={hiddenProductCount}>
                        <Tooltip content={l10n.RESIZER.expandFilters} placement="top">
                          <div
                            ref={!hiddenMediaCount ? hiddenItemsCounterRef : undefined}
                            data-id="hiddenItems"
                            className="toriut-search-filters__hidden-counter"
                            onClick={(e) => expand(e, 'aggregatedBadgeClick')}
                          >
                            <div className={cn('toriut-search-filters__filter-item', {
                              'toriut-search-filters__filter-item--active': !hiddenItemsHaveActiveFilter,
                            })}
                            >
                              <div className="toriut-search-filters__filter-title">{`+${hiddenProductCount}`}</div>
                            </div>
                          </div>
                        </Tooltip>
                      </If>
                    </div>
                  </When>
                  <Otherwise>
                    {
                      productRowItems.map((item, i) => (
                        <SearchFilterItem
                          key={item.id}
                          ref={itemsProductsRef.current[i]}
                          id={item.id}
                          data={item}
                          filterData={selectedFilters[item.id]}
                          confirmedData={confirmedFilters[item.id]}
                          customizingMode={customizingMode}
                          isSelected={checkActive(item.id)}
                          isOpen={checkOpen(item.id)}
                          removable={!pinnedFilterIds.includes(item.id)}
                          filters={FILTERS}
                          rootCollectionId={rootCollection?._id}
                          stackAboveModals={isMediaLibraryModal}
                          onSelectFilter={handleSelectFilter}
                          onConfirmFilter={handleConfirmFilter}
                          onResetFilter={handleResetFilter}
                          onOpen={handleOpen}
                          onClose={handleClose}
                          onRemove={handleRemoveFilter}
                          onDragStart={dragstart}
                          onDragEnter={dragenter}
                          onDragEnd={dragend}
                        />
                      ))
                    }
                    <If condition={mixCollapsedRows && hiddenItemsCount}>
                      <Tooltip content={l10n.RESIZER.expandFilters} placement="top">
                        <div
                          ref={hiddenItemsCounterRef}
                          data-id="hiddenItems"
                          className="toriut-search-filters__hidden-counter"
                          onClick={(e) => expand(e, 'aggregatedBadgeClick')}
                        >
                          <div className={cn('toriut-search-filters__filter-item', {
                            'toriut-search-filters__filter-item--active': !hiddenItemsHaveActiveFilter,
                          })}
                          >
                            <div className="toriut-search-filters__filter-title">{`+${hiddenItemsCount}`}</div>
                          </div>
                        </div>
                      </Tooltip>
                    </If>
                  </Otherwise>
                </Choose>
                <If condition={picsioConfig.isMainApp}>
                  <If condition={customizingMode}>
                    <AddFiltersBtn onAddFilters={handleShowAddFiltersDialog} />
                  </If>
                </If>
                <If condition={!customizingMode && Object.keys(confirmedFilters).length && !resetIsHidden && !isCollapsed}>
                  <div
                    data-id="resetFilters"
                    className="toriut-search-filters__reset-link"
                  >
                    <span
                      className="picsioLink"
                      onClick={(e) => handleResetAllFilters(e, 'expandedView')}
                    >
                      {l10n.SEARCH.resetFiltersBtn}
                    </span>
                  </div>
                </If>
              </div>
            </If>
          </div>
        </If>
        <If condition={isMediaLibraryModal && !customizingMode}>
          <div ref={controlsRef} className="toriut-search-filters__controls">
            <If condition={picsioConfig.isMainApp}>
              <Choose>
                <When condition={isCollapsed && !resetIsHidden}>
                  <div
                    data-id="resetFilters"
                    className="toriut-search-filters__reset-link"
                  >
                    <span
                      className="picsioLink"
                      onClick={(e) => handleResetAllFilters(e, 'minimizedView')}
                    >
                      {l10n.SEARCH.resetFiltersBtn}
                    </span>
                  </div>
                </When>
                <Otherwise>
                  <Tooltip
                    content={l10n.SEARCH.addMoreFilters}
                    placement="top"
                  >
                    <IconButton
                      size="md"
                      className="toriut-search-filters__control-button toriut-search-filters__control-button--add-more"
                      onClick={handleCustomizingMode}
                    >
                      <AddMoreFilters />
                    </IconButton>
                  </Tooltip>
                </Otherwise>
              </Choose>
            </If>
          </div>
        </If>
        <If condition={showExpandControl}>
          <Tooltip placement="top" content={isCollapsed ? l10n.RESIZER.expandFilters : l10n.RESIZER.collapseFilters}>
            <div className="toriut-search-filters__expand-control">
              <Choose>
                <When condition={isCollapsed}>
                  <IconButton onClick={(e) => expand(e, 'arrowHoverClick')}>
                    <ArrowBottomSmall />
                  </IconButton>
                </When>
                <Otherwise>
                  <IconButton onClick={collapse}>
                    <ArrowTopSmall />
                  </IconButton>
                </Otherwise>
              </Choose>
            </div>
          </Tooltip>
        </If>
      </If>
    </div>
  );
};

export default SearchFiltersToriut;
